import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryCityMarketingPage,
  buildIndustryCityMarketingHead,
  type IndustryCityMarketingConfig,
} from "@/components/IndustryCityMarketingPage";
import { PARKER_PROFILE } from "@/lib/cityProfiles/parker";

const CONFIG: IndustryCityMarketingConfig = {
  slug: "plumbing-marketing-parker",
  industry: "Plumbing",
  industryLower: "plumbing",
  ...PARKER_PROFILE,
  metaTitle: "Plumbing Marketing in Parker, CO | SEO, Maps, Ads & Web",
  metaDescription: "Owner-led plumbing marketing for Parker plumbers \u2014 SEO, Google Maps, web design, and ads across Stonegate and Stroh Ranch. Flat $1,650/mo, no contracts.",
  h1: { lead: "Plumbing marketing in", gold: "Parker", trail: "\u2014 from Stonegate to The Pinery" },
  eyebrow: "Parker Plumbing Marketing",
  heroDescription: "We help Parker plumbers own the Map Pack across 80134 and 80138, win 'emergency plumber' and 'water heater replacement' searches, and keep crews booked year-round.",
  intro: "When a pipe bursts in a Stonegate basement at 2 a.m. in January, the homeowner doesn't comparison shop \u2014 they call the first plumber who shows up on their phone. In Parker, that's often a call-center brand or a lead-gen aggregator reselling the job, not a local shop that can actually be there in 45 minutes. Winning Parker plumbing means owning 'emergency plumber near me' in the Map Pack, ranking for high-ticket work like water heater, sewer line, and repipe jobs across Stroh Ranch, The Pinery, Canterberry Crossing, and converting panicked mobile callers instantly. That's what we build \u2014 owner-led, flat $1,650/month, no contracts.",
  painPoints: [
  "Paying lead-gen aggregators per call for Parker jobs you should be getting directly from Google.",
  "Emergency calls going to whichever plumber answers first \u2014 and the Map Pack dominated by franchise brands.",
  "Water heater, sewer line, and repipe jobs in The Pinery going to competitors with stronger review counts."
],
  services: {
  "seo": "Rank for 'plumber Parker,' 'emergency plumber near me,' and 'water heater replacement' across 80134 and 80138.",
  "googleMaps": "Top-3 Map Pack for Parker plumbers \u2014 24/7 hours tuned, service categories dialed in, and steady review velocity.",
  "webDesign": "Hand-coded plumbing sites that load sub-2s on a phone and get a panicked homeowner to tap-to-call in one move.",
  "ppc": "Emergency-focused Google Ads and LSA with call-only bidding that ramps during Parker freeze events.",
  "geoAi": "Get cited when Parker homeowners ask ChatGPT 'who's a reliable plumber near me open now?'",
  "socialMedia": "Trust content \u2014 tech intros, before/after jobs, maintenance tips \u2014 that turns one-time fixes into repeat customers."
},
  trustPoints: [
  "Owner-led from our Colorado Springs HQ \u2014 we know Parker plumbers' market firsthand.",
  "300+ Colorado clients served since 2006 across roofing, plumbing, HVAC, and electrical.",
  "Flat $1,650/month marketing, websites from $1,850 \u2014 published publicly.",
  "Talk to Chris directly. No account managers or offshore handoffs."
],
  testimonial: { quote: "We were paying a lead service for every call and fighting three other plumbers for each one. Now our own Google listing brings the calls \u2014 and they're ours alone.", role: "Plumbing company owner", location: "Parker, CO" },
  faqs: [
  {
    "q": "How do I get more plumbing leads in Parker?",
    "a": "Own the Google Map Pack for 'emergency plumber' and 'plumber near me' across 80134 and 80138, with a website that converts mobile callers in seconds. Add Google Local Services Ads for pay-per-lead calls and stop renting leads from aggregators."
  },
  {
    "q": "How much does plumbing marketing cost in Parker?",
    "a": "Our Growth Marketing package \u2014 SEO, Google Maps, GEO AI, and social \u2014 is $1,650/month flat. A new plumbing website starts at $1,850 one-time, owned outright, with in-house financing available. Prices are public because Parker plumbers deserve a straight answer."
  },
  {
    "q": "How long until I rank in the Parker Map Pack?",
    "a": "Most Parker plumbing clients see Map Pack movement within 60\u2013120 days, and citywide top-3 across Stonegate, Stroh Ranch, The Pinery usually takes 4\u20136 months of consistent work. It compounds month over month."
  },
  {
    "q": "Do you cover Elizabeth, Franktown, and Lone Tree too?",
    "a": "Yes. We build service-area pages and Map Pack signals for every area your trucks reach, including Elizabeth, Franktown, and Lone Tree."
  },
  {
    "q": "When should I ramp up plumbing ads in Parker?",
    "a": "Ahead of winter freeze season (December\u2013February). We build the campaigns in advance so they can go live the moment demand spikes, and scale them back when your schedule is full."
  },
  {
    "q": "Are you actually local to Parker?",
    "a": "We're headquartered in Colorado Springs (3210 E Woodmen Rd) and work Parker weekly. Verified Google Business Profile, 4.9\u2605 average, Colorado-owned since 2006."
  },
  {
    "q": "Do you lock me into a contract?",
    "a": "No. Every engagement is month-to-month \u2014 no early-termination fees. We earn the renewal every month."
  }
],
  related: [
  {
    "to": "/plumbing-marketing-colorado",
    "label": "Plumbing marketing across Colorado",
    "description": "Statewide hub for Colorado plumbers."
  },
  {
    "to": "/parker",
    "label": "Parker digital marketing hub",
    "description": "Every service we offer for Parker businesses."
  },
  {
    "to": "/plumbing-marketing-colorado-springs",
    "label": "Plumbing marketing in Colorado Springs",
    "description": "Same playbook tuned for Colorado Springs plumbers."
  },
  {
    "to": "/plumbing-marketing-castle-rock",
    "label": "Plumbing marketing in Castle Rock",
    "description": "Same playbook tuned for Castle Rock plumbers."
  },
  {
    "to": "/hvac-marketing-parker",
    "label": "HVAC marketing in Parker",
    "description": "Our HVAC playbook for Parker."
  },
  {
    "to": "/pricing",
    "label": "Flat, public pricing",
    "description": "$1,650/mo marketing, websites from $1,850."
  }
],
  showPricing: true,
};

export const Route = createFileRoute("/plumbing-marketing-parker")({
  head: () => buildIndustryCityMarketingHead(CONFIG),
  component: () => <IndustryCityMarketingPage config={CONFIG} />,
});
